import { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Contract, Owner, Contact, Address, Notes, PaymentInstallments, ContractStatus, POINTS_RIGHTS_DUE_DATE, isExplorerMembership } from "@/types";
import { calculatePointPrice } from "@/utils";
import { MembershipStep } from "./multi-step-form/MembershipStep";
import { ContactFormItem } from "./multi-step-form/ContactFormItem";
import { AdditionalContactsStep } from "./multi-step-form/AdditionalContactsStep";
import { AddressStep } from "./multi-step-form/AddressStep";
import { PaymentStep } from "./multi-step-form/PaymentStep";
import { FormOfSurrenderStep } from "./multi-step-form/FormOfSurrenderStep";
import { WelcomeNotesStep } from "./multi-step-form/WelcomeNotesStep";
import { InternalNotesStep } from "./multi-step-form/InternalNotesStep";
import { useContracts } from "@/contexts/ContractContext";
import { toast } from "sonner"; // Add this import
import { convertToUTC2 } from "@/utils/dateUtils";
import { useAuth } from "@/contexts/AuthContext"; // Import useAuth hook
import { StepIndicator } from "./StepIndicator"; // Import the StepIndicator component
import { Country } from "@/types"; // Import Country enum

interface MultiStepAddContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<Contract, '_id'>) => void;
  contractData?: Contract | null;
  isLoading?: boolean;
  isEditMode?: boolean; // Add this prop to indicate edit mode
}

export function MultiStepAddContractModal({ isOpen, onClose, onSubmit, contractData, isLoading: externalLoading, isEditMode }: MultiStepAddContractModalProps) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numberOfInstallments, setNumberOfInstallments] = useState<number | "none">("none"); // Default to "none" - let user choose
  const [paymentInstallments, setPaymentInstallments] = useState<PaymentInstallments[]>([]);
  const [additionalContacts, setAdditionalContacts] = useState<number>(0);
  const [editorContent, setEditorContent] = useState('');
  const [internalNotesContent, setInternalNotesContent] = useState('');
  const [paymentType, setPaymentType] = useState<'details' | 'installments'>('installments');
  const formRef = useRef<HTMLFormElement>(null);
  const { updateContract } = useContracts();
  const { user } = useAuth(); // Get current user

  // Check if user is a manager
  const isManager = user?.role === 'manager';
  // Calculate total steps based on user role
  const totalSteps = isManager ? 7 : 8;

  // Step titles for the step indicator
  const stepTitles = [
    "Membership",
    "Owner Details",
    "Additional Contacts",
    "Address",
    "Payment",
    "Form of Surrender",
    "Reason for purchase",
    isManager ? "" : "Addtional Offer Notes",
  ];

  const defaultValues = {
    status: ContractStatus.New,
    firstYearUse: new Date().getFullYear() + 1, // Default to 2026
    pointsRightsDueDate: POINTS_RIGHTS_DUE_DATE,
    termYears: 10,
    archiveStatus: 'pending', // Initialize archiveStatus to 'pending' by default
    owner: {
      address: {
        buildingNumber: '',
        street: '',
        town: '',
        stateProvince: '',
        postCode: '',
        country: Country.SPAIN, // Default country set to Spain
      }
    }
  };

  const methods = useForm<Omit<Contract, '_id'>>({
    defaultValues: contractData || defaultValues,
    mode: "onChange"
  });

  const purchasePrice = methods.watch('purchasePrice') || 0;
  const membershipLevel = methods.watch('owner.membershipLevel');
  const paymentTerms = methods.watch('paymentTerms');
  const isExplorer = isExplorerMembership(membershipLevel);

  // Watch form data to calculate joint member count
  const watchOwner = methods.watch('owner');
  const watchAdditionalContacts = methods.watch('additionalContacts');
  const jointMemberCount = (watchOwner?.isJointMember ? 1 : 0) +
    (watchAdditionalContacts || []).filter((contact: any) => contact?.isJointMember).length;

  useEffect(() => {
    if (isOpen) {
      // Reinitialize the form with contractData when the modal is opened
      if (contractData) {
        methods.reset(contractData);

        const installmentsLength = contractData.paymentInstallments?.length ?? 0;
        setNumberOfInstallments(
          installmentsLength > 0
            ? installmentsLength
            : "none"
        );

        setPaymentInstallments(contractData.paymentInstallments || []);
        setEditorContent(contractData.notes?.welcomeEmailNotes || "");
        setInternalNotesContent(contractData.notes?.internalNotes || "");

        setPaymentType(
          installmentsLength > 0 ? "installments" : "details"
        );

        if (Array.isArray(contractData.additionalContacts)) {
          setAdditionalContacts(contractData.additionalContacts.length);
        }
      }
    } else {
      // Reset the form state when the modal is closed
      setStep(1);
      methods.reset(defaultValues);
      setNumberOfInstallments("none"); // Reset to "none" - let user choose
      setPaymentInstallments([]);
      setAdditionalContacts(0);
      setEditorContent("");
      setInternalNotesContent("");
      setPaymentType("installments");
    }
  }, [isOpen, contractData, methods]);

  // useEffect(() => {
  //   if (contractData) {
  //     methods.reset(contractData);

  //     if (contractData.owner?.membershipLevel) {
  //       methods.setValue("owner.membershipLevel", contractData.owner.membershipLevel);
  //     }

  //     if (Array.isArray(contractData.additionalContacts)) {
  //       setAdditionalContacts(contractData.additionalContacts.length);
  //     }

  //     if (Array.isArray(contractData.paymentInstallments) && contractData.paymentInstallments.length > 0) {
  //       setPaymentInstallments(contractData.paymentInstallments);
  //       setNumberOfInstallments(contractData.paymentInstallments.length);
  //       setPaymentType('installments');
  //     } else if (contractData.notes?.paymentNotes) {
  //       setPaymentType('details');
  //     }

  //     if (contractData.notes?.welcomeEmailNotes) {
  //       setEditorContent(contractData.notes.welcomeEmailNotes);
  //     }

  //     if (contractData.notes?.internalNotes) {
  //       setInternalNotesContent(contractData.notes.internalNotes);
  //     }

  //     if (contractData.owner) {
  //       methods.setValue("owner", {
  //         ...contractData.owner,
  //         title: contractData.owner.title || '',
  //         firstName: contractData.owner.firstName || '',
  //         middleName: contractData.owner.middleName || '',
  //         lastName: contractData.owner.lastName || '',
  //         email: contractData.owner.email || '',
  //         membershipNumber: contractData.owner.membershipNumber,
  //         membershipLevel: contractData.owner.membershipLevel,
  //         address: {
  //           buildingNumber: contractData.owner.address?.buildingNumber || '',
  //           street: contractData.owner.address?.street || '',
  //           town: contractData.owner.address?.town || '',
  //           stateProvince: contractData.owner.address?.stateProvince || '',
  //           postCode: contractData.owner.address?.postCode || '',
  //           country: contractData.owner.address?.country || ''
  //         }
  //       });
  //     }
  //   }
  // }, [contractData, methods]);

  useEffect(() => {
    setPaymentInstallments(prev => {
      if (numberOfInstallments === "none" || numberOfInstallments <= 0) {
        return [];
      }

      // Preserve existing installments and their IDs
      const newInstallments = prev.slice(0, numberOfInstallments);

      // Add new installments if the number increases
      while (newInstallments.length < numberOfInstallments) {
        newInstallments.push({
          date: '',
          amount: 0,
        });
      }

      return newInstallments;
    });
  }, [numberOfInstallments]);

  useEffect(() => {
    if (membershipLevel) {
      const pointPrice = calculatePointPrice(membershipLevel);
      methods.setValue("pointPrice", pointPrice);
      methods.setValue("owner.membershipLevel", membershipLevel);
    }
  }, [membershipLevel, methods]);

  const validateStep = async (currentStep: number): Promise<boolean> => {
    const isValid = await methods.trigger();
    return isValid;
  };

  const processContractData = (data: any): Omit<Contract, '_id'> => {
    const address = {
      buildingNumber: data.owner.address.buildingNumber || '',
      street: data.owner.address.street || '',
      town: data.owner.address.town || '',
      stateProvince: data.owner.address.stateProvince || '',
      postCode: data.owner.address.postCode || '',
      country: data.owner.address.country || ''
    };

    const processedPaymentInstallments = paymentInstallments.map((installment) => ({
      ...installment,
      date: installment.date ? convertToUTC2(installment.date) : '',
      amount: Number(installment.amount) || 0,
    }));

    // For managers, preserve existing agreement number if not provided
    // For Explorer memberships, set agreement number to null
    let agreementNumber = isExplorer ? null : (Number(data.agreementNumber) || 0);
    if (!isExplorer && isManager && contractData && contractData.agreementNumber) {
      agreementNumber = contractData.agreementNumber;
    }

    return {
      ...data,
      purchasePrice: Number(data.purchasePrice) || 0,
      pointsRightsPurchased: Number(data.pointsRightsPurchased) || 0,
      paymentTerms: data.paymentTerms || "Monthly",
      paymentInstallments: processedPaymentInstallments,
      notes: {
        paymentNotes: data.notes?.paymentNotes || "",
        welcomeEmailNotes: isManager ? "" : editorContent || "",
        internalNotes: data.notes?.internalNotes || "",
        formOfSurrenderNotes: data.notes?.formOfSurrenderNotes || "",
      },
      additionalContacts: data.additionalContacts || [],
      status: isEditMode ? data.status : ContractStatus.New,
      isSendWelcomeEmail: data.isSendWelcomeEmail || false,
      isTestSubmission: data.isTestSubmission || false,
      termYears: isExplorer ? 3 : (Number(data.termYears) || 10),
      archiveStatus: data.archiveStatus || 'pending',
      owner: {
        ...data.owner,
        membershipNumber: isExplorer ? null : (Number(data.owner.membershipNumber) || 0),
        membershipLevel: data.membershipLevel || data.owner.membershipLevel,
        address: address,
        prospectId: data.owner?.prospectId || undefined,
      },
      agreementNumber: agreementNumber,
      pointsRightsDueDate: Number(data.pointsRightsDueDate) || POINTS_RIGHTS_DUE_DATE,
      firstYearUse: Number(data.firstYearUse) || new Date().getFullYear() + 1, // Default to 2026
      isRealEstateOwner: Boolean(data.owner?.isRealEstate),
      formOfSurrenders: (data.formOfSurrenders || []).filter(
        (entry: any) => entry?.agreementNumber || entry?.product
      ),
    };
  };

  const saveContractSummary = async () => {
    try {
      setIsLoading(true);
      const data = methods.getValues();
      const updatedContractData = processContractData(data);

      if (contractData && contractData._id) {
        await updateContract(contractData._id, updatedContractData);
        // toast.success("Contract updated successfully");
      } else {
        onSubmit(updatedContractData);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error saving contract:", error);
      setIsLoading(false);
    }
  };

  const onSubmitForm = async (data: any) => {
    if (isSubmitting) return;

    const isValid = await validateStep(step);
    if (!isValid) {
      return;
    }

    const finalStep = isManager ? 7 : 8;
    if (step < finalStep) {
      setStep(prev => prev + 1);
      return;
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);

      const updatedContractData = processContractData(data);

      if (contractData && contractData._id) {
        await updateContract(contractData._id, updatedContractData);
        toast.success("Contract updated successfully");
      } else {
        onSubmit(updatedContractData);
      }

      onClose();
      setIsSubmitting(false);
      setIsLoading(false);
      setStep(1);
      formRef.current?.reset();
      methods.reset(defaultValues);
    } catch (error) {
      console.error("Error saving contract:", error);
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const updatePaymentInstallment = (index: number, field: 'date' | 'amount', value: string | number) => {
    setPaymentInstallments(prevInstallments => {
      const newPaymentInstallments = [...prevInstallments];

      // Ensure the array has enough elements and initialize if needed
      while (newPaymentInstallments.length <= index) {
        newPaymentInstallments.push({ date: '', amount: 0 });
      }

      // Properly update the specific index with the new value
      newPaymentInstallments[index] = {
        ...newPaymentInstallments[index],
        [field]: value
      };

      // Debug log to verify each index is being updated correctly
      console.log(`Updated index ${index}, field ${field}:`, newPaymentInstallments);

      return newPaymentInstallments;
    });
  };

  const prevStep = () => setStep(prev => prev - 1);

  // Calculate progress percentage based on total steps
  const progressPercentage = (step / totalSteps) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] lg:max-w-[1000px] bg-background max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Editing Contract" : `Add New Contract - Step ${step} of ${totalSteps}`}
          </DialogTitle>
          <DialogDescription>
            Fill in the contract details. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        <Progress value={progressPercentage} className="mb-4" />

        <StepIndicator
          currentStep={step}
          totalSteps={totalSteps}
          stepTitles={stepTitles}
        />

        <FormProvider {...methods}>
          <form ref={formRef} onSubmit={methods.handleSubmit(onSubmitForm)} className="space-y-4">
            {step === 1 && <MembershipStep />}
            {step === 2 && (
              <ContactFormItem
                prefix="owner"
                isOwner={true}
                jointMemberCount={jointMemberCount}
                isExplorer={isExplorer}
              />
            )}
            {step === 3 && (
              <AdditionalContactsStep
                additionalContacts={additionalContacts}
                onAdditionalContactsChange={setAdditionalContacts}
                isExplorer={isExplorer}
              />
            )}
            {step === 4 && <AddressStep />}
            {step === 5 && (
              <PaymentStep
                numberOfInstallments={numberOfInstallments}
                onInstallmentsChange={(value) =>
                  setNumberOfInstallments(value === "none" ? "none" : Number(value))
                }
                paymentInstallments={paymentInstallments}
                onPaymentInstallmentChange={updatePaymentInstallment}
                paymentType={paymentType}
                onPaymentTypeChange={setPaymentType}
                isEditMode={isEditMode}
                isExplorer={isExplorer}
              />
            )}
            {step === 6 && <FormOfSurrenderStep />}
            {step === 7 && (
              <InternalNotesStep
                editorContent={internalNotesContent}
                onEditorContentChange={setInternalNotesContent}
              />
            )}
            {step === 8 && !isManager && (
              <WelcomeNotesStep
                editorContent={editorContent}
                onEditorContentChange={setEditorContent}
              />
            )}

            <DialogFooter>
              {step > 1 && (
                <Button type="button" variant="outline" onClick={prevStep}>
                  Previous
                </Button>
              )}
              {isEditMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={saveContractSummary}
                  disabled={isLoading || externalLoading}
                >
                  Save
                </Button>
              )}
              <Button
                type="submit"
                disabled={isLoading || externalLoading}
              >
                {isLoading || externalLoading
                  ? "Saving..."
                  : step < (isManager ? 7 : 8)
                    ? "Next"
                    : isEditMode
                      ? "Close"
                      : "Save and Close"}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}